import { useCallback } from 'react'
import { AutocompleteInput, Listbox, SelectContext, SelectValue, useAsyncSelectContext } from '@cargomart/ui'
import cn from 'classnames'

import {
  defaultLoadingMessage,
  defaultNoOptionsMessage,
  defaultNoOptionsMessageWithEmptySearch
} from './helpers/defaultValues'
import { DefaultSelectPostfix } from './DefaultSelectPostfix'
import { OptionListFooter } from './OptionListFooter'
import { IBaseAsyncSelect } from './types'

export const BaseAsyncSelect = <Value, Multiple extends boolean = false>({
  children,
  placeholder,
  fullscreenName,
  className,
  size,
  error,
  tabIndex,
  prefix,
  postfix,
  'data-position-in-group-v': dataPositionInGroupV,
  'data-position-in-group-h': dataPositionInGroupH,
  hasDropDownIcon,
  optionsLength,
  total = 0,
  isLoading = false,
  isClearable,
  hasInfinityScroll,
  noOptionsMessage = defaultNoOptionsMessage,
  validateForEmpty = (value) => Boolean(value),
  noOptionsMessageWithEmptySearch = defaultNoOptionsMessageWithEmptySearch,
  loadingMessage = defaultLoadingMessage,
  isDisabled,
  onChange,
  readOnly,
  defaultOption,
  ...props
}: IBaseAsyncSelect<Value, Multiple>) => {
  const handleChange = useCallback(
    (option?: SelectValue<Value, Multiple> | null) => {
      onChange((option as Value) || defaultOption || undefined)
    },
    [onChange]
  )

  const { context, commonInputProps, handleClear } = useAsyncSelectContext({
    disabled: isDisabled,
    onChange: handleChange,
    ...props
  })

  const hasSearchValue = validateForEmpty(commonInputProps.value)
  const customNoOptionsMessage = hasSearchValue ? noOptionsMessage : noOptionsMessageWithEmptySearch

  const FooterComponent = (
    <OptionListFooter
      hasInfinityScroll={hasInfinityScroll}
      isLoading={isLoading}
      loadingMessage={loadingMessage}
      noOptionsMessage={customNoOptionsMessage}
      optionsLength={optionsLength}
      total={total}
    />
  )

  return (
    <SelectContext.Provider value={context}>
      <AutocompleteInput
        {...commonInputProps}
        className={cn(className)}
        data-position-in-group-h={dataPositionInGroupH}
        data-position-in-group-v={dataPositionInGroupV}
        invalid={!!error}
        placeholder={placeholder}
        prefix={prefix}
        readOnly={readOnly}
        size={size}
        tabIndex={tabIndex}
        postfix={
          <DefaultSelectPostfix
            error={error}
            handleClear={handleClear}
            hasDropDownIcon={hasDropDownIcon}
            hasOption={!!context?.value?.length}
            isClearable={isClearable}
            isDisabled={isDisabled}
            isLoading={isLoading}
            isOpen={context.isOpen}
            postfix={postfix}
          />
        }
      />
      <Listbox afterList={FooterComponent}>{children}</Listbox>
    </SelectContext.Provider>
  )
}
